/**
 * standlarListService.js — "Standlar" Listesi Servis Dosyası
 *
 * İKİ AYRI YARI VAR:
 *
 * 1) CANLI (2026-09-15) — `/Management/Standlar` listesinin kullandığı
 *    `getTumStandlarList` + `getStandDurumu`. Liste `GET /standTalep/list` roster'ını
 *    standOverviewService.getStandOverviewList(forceLive) üzerinden okur (satır
 *    eşlemesi orada TEK yerde, Projeler > Stand Listesi ile aynı). Stand durumu satır
 *    başına ÇEKİLMEZ — yalnızca kullanıcı "Durum" butonuna basınca, o tek stand için.
 *
 * 2) MOCK — aşağıdaki `_mockStandlar` + CRUD fonksiyonları. Artık hiçbir rotadan
 *    çağrılmıyor; /new-page scaffold'unun referans modal/detay örnekleri
 *    (TumStandlarFormModal.jsx, TumStandlarDetailPage.jsx) için duruyor.
 *
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getStandOverviewList } from './standOverviewService';
import { getProjectByStandId, isRevisionUnfinished, isRevisionAwaitingUs } from './projectService';

// ═════════════════════════════════════════════════════════════════════════════
// 1) CANLI LİSTE
// ═════════════════════════════════════════════════════════════════════════════

// ─── Alan (m²) — ÖRNEK DEĞER ─────────────────────────────────────────────────
// `standArea` KROKİDEN ölçülür ve çoğu fuarda null (298'de hiç yok — bkz.
// util/knowledgebase/stand-list-pills.md). Kroki gelene kadar liste boş bir sütun
// göstermesin diye standId'den TÜRETİLMİŞ, sabit (her yenilemede aynı) bir örnek
// değer basılıyor ve sayfa bunu "örnek" diye işaretliyor. Kroki ölçümü varsa o
// kazanır. Krokiler dolunca bu bayrağı false yapın — sütun gerçek değere/—'ye döner.
export const USE_MOCK_AREA = true;

function mockAreaFor(standId) {
  // 9–120 m² arası, 3'ün katları (3×3 kabinden 10×12 ada standa kadar).
  const n = Number(standId) || 0;
  return 9 + ((n * 37) % 38) * 3;
}

/**
 * Seçili fuarın stand roster'ı, sayfalı. Canlı — her fuar için (salt okuma).
 * GET /standTalep/list?fuarId&search&salonId&hasDebt&isProblem&isForbidden&decoratorFirmaId&page&size
 *
 * Her satıra `areaM2` + `areaIsMock` eklenir; geri kalan alanlar
 * standOverviewService.mapRosterRow'dan olduğu gibi gelir (firmaName, firmaShortName,
 * dekoratorFirmaName, standTypeName/standCategory, constructionPermit, workPermit…).
 *
 * isForbidden/decoratorFirmaId backlog #69(1): backend #68'den beri destekleniyordu ama bu sayfa
 * ikisini de göndermiyordu — filtre panelinde artık ikisi de var.
 *
 * @param {object} p { fuarId, search, salonId, hasDebt, isProblem, isForbidden, decoratorFirmaId, page (1-based), limit }
 * @returns {{ total, page, limit, records }}
 */
export async function getTumStandlarList({
  fuarId, search = '', salonId, hasDebt, isProblem, isForbidden, decoratorFirmaId, page = 1, limit = 25,
} = {}) {
  const data = await getStandOverviewList({
    fuarId, search, salonId, hasDebt, isProblem, isForbidden, decoratorFirmaId, page, limit, forceLive: true,
  });
  const records = (data.records || []).map((r) => {
    const measured = r.standArea != null;
    return {
      ...r,
      areaM2: measured ? r.standArea : (USE_MOCK_AREA ? mockAreaFor(r.standId) : null),
      areaIsMock: !measured && USE_MOCK_AREA,
    };
  });
  return { ...data, total: data.total || 0, records };
}

/**
 * TEK bir standın "durumu" — liste satırının zaten taşıdığı izinlerin YANINA, roster'da
 * olmayan proje tarafı: Proje Beyanı aşaması, gerekli belgelerin onay durumu,
 * bitmemiş revizyonlar, anlaşma/finans. Satır başına değil, butona basınca çağrılır.
 * GET /projects/by-stand/{standId} (+ GET /standTalep/borcDurumu/{standId})
 *
 * Proje kaydı yoksa `project: null` döner — hata değil, standların çoğunda proje yok.
 */
export async function getStandDurumu(standId) {
  const [project, borc] = await Promise.all([
    getProjectByStandId(standId, { forceLive: true }),
    axios.get(`${JAVA_BACKEND_API_URL}/standTalep/borcDurumu/${standId}`, { headers: getAuthHeader() })
      .then((res) => res.data)
      .catch(() => null), // yardımcı bilgi — düşerse satırın legacy borç bayrağı kullanılır
  ]);

  if (!project) return { project: null, hasDebt: borc?.hasDebt ?? null };

  const approvals = new Map((project.fileApprovals || []).map((fa) => [fa.standFileId, fa.status]));
  const documents = (project.requiredFiles || []).map((f) => ({
    standFileId: f.stand_file_id,
    label: f.stand_file_name,
    status: approvals.get(f.stand_file_id) ?? 0,
    uploadedCount: (project.uploadedFiles || []).filter((u) => u.standFileId === f.stand_file_id).length,
  }));
  const threads = (project.revisions || []).filter((r) => Array.isArray(r.messages));

  return {
    project: {
      id: project.id,
      standTypeName: project.standTypeName ?? null,
      dealStatus: project.dealStatus ?? null,
      financeApproval: project.financeApproval ?? null,
      projeBeyani: project.projeBeyani ?? null,
      documents,
      openRevisions: threads.filter(isRevisionUnfinished).length,
      awaitingUs: threads.filter(isRevisionAwaitingUs).length,
      updatedAt: project.updatedAt ?? null,
    },
    hasDebt: borc?.hasDebt ?? null,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// 2) MOCK — /new-page referans örnekleri (rotaya bağlı değil)
// ═════════════════════════════════════════════════════════════════════════════

const BASE = `${JAVA_BACKEND_API_URL}/standlar`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

let _mockStandlar = [
  {
    standId: 57891,
    standName: '2.2A10',
    salonId: 2,
    salonName: 'Salon 2',
    firmaId: 101,
    firmaName: 'RALFTEKS TEKS. GIDA TAR. ÜRÜN. İNŞ. EMLAK SAN. VE TİC. A.Ş.',
    decoratorFirmaId: 202,
    decoratorFirmaName: 'ZABUN GRUP - FIX EXPO',
    fuarId: 3,
  },
  {
    standId: 58135,
    standName: '2.2A100',
    salonId: 2,
    salonName: 'Salon 2',
    firmaId: 102,
    firmaName: 'TAREK ALAADDIN ABDERLKADER MOHAMED RIAD',
    decoratorFirmaId: 202,
    decoratorFirmaName: 'ZABUN GRUP - FIX EXPO',
    fuarId: 3,
  },
  {
    standId: 58200,
    standName: '1.1B05',
    salonId: 1,
    salonName: 'Salon 1',
    firmaId: 103,
    firmaName: 'NEHİR İŞ SAĞLIĞI VE GÜVENLİĞİ EĞİTİM DANIŞ. HİZ. TİC. LTD. ŞTİ.',
    decoratorFirmaId: null,
    decoratorFirmaName: null,
    fuarId: 3,
  },
  {
    standId: 58311,
    standName: '3.3C22',
    salonId: 3,
    salonName: 'Salon 3',
    firmaId: 104,
    firmaName: 'SEDEF GRUP',
    decoratorFirmaId: 205,
    decoratorFirmaName: 'ARTE DEKORASYON',
    fuarId: 3,
  },
  {
    standId: 58402,
    standName: '1.1B12',
    salonId: 1,
    salonName: 'Salon 1',
    firmaId: 105,
    firmaName: 'TEST FİRMA',
    decoratorFirmaId: null,
    decoratorFirmaName: null,
    fuarId: 3,
  },
];

let _nextId = 60000;

/**
 * Paginated "Tüm Standlar" list, scoped by fuarId.
 * GET /standlar?fuarId=3&page=0&size=25&search=...
 */
export async function getStandlarList(params = {}) {
  const { fuarId, page = 0, size = 25, search = '' } = params;

  if (USE_MOCK_DATA) {
    await mockDelay();

    let filtered = _mockStandlar.filter((s) => {
      if (fuarId && s.fuarId !== Number(fuarId)) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          s.standName.toLowerCase().includes(q) ||
          (s.firmaName || '').toLowerCase().includes(q) ||
          (s.decoratorFirmaName || '').toLowerCase().includes(q) ||
          (s.salonName || '').toLowerCase().includes(q)
        );
      }
      return true;
    });

    const totalElements = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / size));
    const start = page * size;
    const content = filtered.slice(start, start + size);

    return {
      content,
      totalElements,
      totalPages,
      currentPage: page,
      first: page === 0,
      last: page >= totalPages - 1,
    };
  }

  try {
    const response = await axios.get(BASE, { params, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching standlar list:', error);
    throw error;
  }
}

/** GET /standlar/{standId} */
export async function getStandlarDetail(standId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const stand = _mockStandlar.find((s) => s.standId === Number(standId));
    if (!stand) throw new Error('Stand bulunamadı');
    return { ...stand };
  }

  try {
    const response = await axios.get(`${BASE}/${standId}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching stand detail:', error);
    throw error;
  }
}

/** POST /standlar */
export async function createStandlar(payload) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const newStand = { standId: _nextId++, ...payload };
    _mockStandlar = [newStand, ..._mockStandlar];
    return newStand;
  }

  try {
    const response = await axios.post(BASE, payload, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error creating stand:', error);
    throw error;
  }
}

/** PUT /standlar/{standId} */
export async function updateStandlar(standId, payload) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const idx = _mockStandlar.findIndex((s) => s.standId === Number(standId));
    if (idx === -1) throw new Error('Stand bulunamadı');
    _mockStandlar[idx] = { ..._mockStandlar[idx], ...payload };
    return { ..._mockStandlar[idx] };
  }

  try {
    const response = await axios.put(`${BASE}/${standId}`, payload, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error updating stand:', error);
    throw error;
  }
}

/** DELETE /standlar/{standId} */
export async function deleteStandlar(standId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    _mockStandlar = _mockStandlar.filter((s) => s.standId !== Number(standId));
    return { success: true };
  }

  try {
    const response = await axios.delete(`${BASE}/${standId}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error deleting stand:', error);
    throw error;
  }
}
